import { app, ojs, payload, state } from "modular-openscriptjs";
import {
    Middleware,
    PAGES,
    analytics,
    api,
    uc,
    gc,
    dc,
    resolveCurrentPageUsing,
} from "./declarations";
import {
    empty,
    fireEvent,
    leaveEchoChannel,
    listenToEchoEvent,
    scrollToTop,
    redirect,
    setActiveNav,
} from "./helpers";
import App from "./components/App";

// Page Imports
import Index from "./components/Pages/Index";
import LoginPage from "./components/Pages/LoginPage";
import Page404 from "./components/Pages/Page404";
import Wallet from "./components/Pages/Wallet";
import TransfersPage from "./components/Pages/Transfers";
import Setting from "./components/Pages/Setting";
import Profile from "./components/Pages/Users/Profile";
import EditProfile from "./components/Pages/Users/EditProfile";
import PasswordPage from "./components/Pages/Users/Password";
import ForgotPassword from "./components/Pages/Users/ForgotPassword";
import ResetPassword from "./components/Pages/Users/ResetPassword";
import OtpAuthPage from "./components/Pages/OtpAuth";
import SelectCompany from "./components/Pages/SelectCompany";
import Notifications from "./components/Pages/Notifications";

import ProcessOrder from "./components/Pages/ProcessOrder";
import Review from "./components/Pages/Review";
import OrderDetails from "./components/Pages/OrderDetails";
import Awards from "./components/Pages/Awards";
import Orders from "./components/Pages/Orders";
import ConfirmMpesaPayment from "./components/Forms/ConfirmMpesaPayment";
import PaymentPage from "./components/Pages/PaymentPage";
import HashtagLocations from "./components/Pages/HashtagLocations";

const router = app("router");
const h = app("h");
const broker = app("broker");
const $e = app("$e");

ojs(
    App,
    Index,
    LoginPage,
    Page404,
    Wallet,
    TransfersPage,
    Setting,
    Profile,
    EditProfile,
    PasswordPage,
    ForgotPassword,
    ResetPassword,
    OtpAuthPage,
    SelectCompany,
    ProcessOrder,
    Review,
    OrderDetails,
    Awards,
    Orders,
    ConfirmMpesaPayment,
    PaymentPage,
    Notifications,
    HashtagLocations,
);

const renderInApp = (callback) => {
    resolveCurrentPageUsing(() => callback());

    return h.App(gc.appLoadedAt, {
        parent: gc.root,
        resetParent: router.reset,
        reconcileParent: true,
    });
};

const m = new Middleware();

router.default(() => router.to("page-not-found"));

router.on("/404", () => renderInApp(() => h.Page404()), "page-not-found");

router
    .orOn(
        ["/", "/index"],
        () => {
            setActiveNav('home');
            let f = () => {
                renderInApp(() => h.Index(gc.auth));
                analytics.pageView("Index");
            };

            if (gc.auth.value) {
                m.auth()?.company(f);
            } else {
                f();
            }
        },
        ["home", "index"],
    )
    .on(
        "/companies/select",
        () => {
            setActiveNav('switch-company');
            renderInApp(() => h.SelectCompany());
        },
        "company.select",
    )
    .on(
        "/wallet",
        () => {
            setActiveNav('wallet');
            m.auth()?.company(() => {
                renderInApp(() => h.Wallet());
                analytics.pageView(PAGES.WALLET);
            });
        },
        "user.view-wallet",
    )
    .on(
        "/settings",
        () => {
            setActiveNav('settings');
            m.auth(() => {
                renderInApp(() => h.Setting());
                fireEvent($e.setting.needs.information);
                analytics.pageView(PAGES.SETTINGS);
            });
        },
        "user.settings",
    )
    .on(
        "/orders",
        () => {
            setActiveNav('orders');
            m.auth().company(() => {
                renderInApp(() => h.Orders(state(window.currentCompany?.id)));
                analytics.pageView(PAGES.PROCESS_ORDERS);
            });
        },
        "company.orders",
    )
    .on(
        "/reviews",
        () => {
            setActiveNav('reviews');
            m.auth(() => {
                renderInApp(() => h.Review());
                analytics.pageView(PAGES.REVIEWS);
            });
        },
        "rider.reviews",
    )
    .on(
        "/awards",
        () => {
            setActiveNav('awards');
            m.auth(() => {
                renderInApp(() => h.Awards());
                analytics.pageView(PAGES.AWARDS);
            });
        },
        "user.awards",
    )
    .on(
        "/notifications",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.Notifications());
                analytics.pageView("Notifications");
            });
        },
        "user.notifications",
    );

router.on(
    "/hashtag-locations",
    () => {
        setActiveNav('hashtag-locations');
        m.auth()?.company(() => {
            renderInApp(() => h.HashtagLocations());
            analytics.pageView(PAGES.HASHTAGS_LOCATION);
        });
    },
    "system.hashtag-locations",
);

router.on(
    "/login",
    () => {
        setActiveNav('login');
        renderInApp(() => h.LoginPage());
        analytics.pageView("Login");
    },
    "user.login",
);

router.prefix("users").group(() => {
    router.on(
        "/profile",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.Profile(gc.user));
                analytics.pageView("View Profile");
            });
        },
        "user.view-profile",
    );

    router.on(
        "/profile/edit",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.EditProfile(gc.user));
                analytics.pageView("Edit Profile");
            });
        },
        "user.edit-profile",
    );

    router.on(
        "/profile/change-password",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.PasswordPage());
                analytics.pageView("Change Password");
            });
        },
        "user.change-password",
    );

    router.on(
        "/forgot-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ForgotPassword());
            analytics.pageView("Forgot Password");
        },
        "user.forgot-password",
    );

    router.on(
        "/reset-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ResetPassword());
            analytics.pageView("Reset Password");
        },
        "user.reset-password",
    );

    router.on(
        "/capa-transfers",
        () => {
            setActiveNav('wallet');
            m.auth()?.company(() => {
                renderInApp(() => h.TransfersPage());
                analytics.pageView("Transfer History");
            });
        },
        "user.capa-transfers",
    );

    router.on(
        "/otp",
        () => {
            setActiveNav('profile');
            m.auth(() => {
                renderInApp(() => h.OtpAuthPage());
                analytics.pageView("OtpAuth");
            });
        },
        "user.verify-identity",
    );
});

router.orOn(
    ["payments/pay/{amount}/{currency}", "payment/collect/{amount}/{currency}"],
    () => {
        setActiveNav('wallet');
        m.auth(() => {
            renderInApp(() => h.PaymentPage());

            analytics.pageView("Collect Payment");
        });
    },
    ["payments.collect"],
);

router.prefix("payment").group(() => {
    router.on("mpesa/process", () => {
        setActiveNav('wallet');
        m.auth(() => {
            renderInApp(() => h.ConfirmMpesaPayment());
            analytics.pageView("Mpesa Payment Confirmation");
        });
    });
});
